// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.11.7
//   protoc               v5.29.6
// source: evaluation.proto

import type { WorkflowState } from './case_record';
import type { Sort } from './common';

export const protobufPackage = 'compasspb';

/** EvaluatorType defines who performed the evaluation. */
export enum EvaluatorType {
  EVALUATOR_TYPE_UNSPECIFIED = 0,
  EVALUATOR_TYPE_AI = 1,
  EVALUATOR_TYPE_HUMAN = 2,
}

/** EvaluationStatus tracks the lifecycle state of a QC evaluation. */
export enum EvaluationStatus {
  EVALUATION_STATUS_UNSPECIFIED = 0,
  EVALUATION_STATUS_CREATED = 1,
  EVALUATION_STATUS_STARTED = 2,
  EVALUATION_STATUS_COMPLETED = 3,
  EVALUATION_STATUS_CANCELLED = 4,
}

/** CallDirection defines the direction of the call being evaluated. */
export enum CallDirection {
  CALL_DIRECTION_UNSPECIFIED = 0,
  CALL_DIRECTION_INBOUND = 1,
  CALL_DIRECTION_OUTBOUND = 2,
  CALL_DIRECTION_INTERNAL = 3,
}

/**
 * EvaluationFlags are optional boolean flags for QC evaluations.
 * Each flag is optional — only flags present in the request are updated.
 */
export interface EvaluationFlags {
  escalation?: boolean | undefined;
  /** Compliance Failed */
  compliance_failed?: boolean | undefined;
}

/** EvaluationReasons provides explanations for flags and metadata values. */
export interface EvaluationReasons {
  /** Reason for escalation flag */
  escalation?: string | undefined;
  /** Reason for compliance_failed flag */
  compliance_failed?: string | undefined;
  /** Reasons for metadata values, keyed by metadata field name */
  metadata?: { [key: string]: string } | undefined;
}

export interface EvaluationReasons_MetadataEntry {
  key: string;
  value: string;
}

/**
 * EvaluationReviewInput is the client-facing request payload for submitting a review decision.
 * reviewed_by and reviewed_at are intentionally absent — the server stamps them
 * from the authenticated user context and current time.
 */
export interface EvaluationReviewInput {
  /** true = accepted (value is correct), false = rejected (value was wrong) */
  accepted?: boolean | undefined;
  /** The original value before review (bool for flags, string for metadata) */
  previous_value?: any | undefined;
  /** Explanation for the review decision */
  reason?: string | undefined;
}

/** EvaluationReviewsInput holds per-flag and per-metadata review inputs for a request. */
export interface EvaluationReviewsInput {
  /** Review for escalation flag */
  escalation?: EvaluationReviewInput | undefined;
  /** Review for compliance_failed flag */
  compliance_failed?: EvaluationReviewInput | undefined;
  /** Reviews for metadata values, keyed by metadata field name */
  metadata?: { [key: string]: EvaluationReviewInput } | undefined;
  /** Reviews for scorecard findings, keyed by metric_key */
  findings?: { [key: string]: EvaluationFindingReviewInput } | undefined;
}

export interface EvaluationReviewsInput_MetadataEntry {
  key: string;
  value?: EvaluationReviewInput | undefined;
}

export interface EvaluationReviewsInput_FindingsEntry {
  key: string;
  value?: EvaluationFindingReviewInput | undefined;
}

/**
 * EvaluationReview is the full review record returned in responses.
 * Includes server-stamped reviewer identity and timestamp.
 */
export interface EvaluationReview {
  /** true = accepted (value is correct), false = rejected (value was wrong) */
  accepted?: boolean | undefined;
  /** The original value before review (bool for flags, string for metadata) */
  previous_value?: any | undefined;
  /** Explanation for the review decision */
  reason?: string | undefined;
  /** User ID of the reviewer (server-stamped) */
  reviewed_by?: string | undefined;
  /** When the review was performed (server-stamped) */
  reviewed_at?: string | undefined;
}

/** EvaluationReviews holds per-flag and per-metadata review records in responses. */
export interface EvaluationReviews {
  /** Review for escalation flag */
  escalation?: EvaluationReview | undefined;
  /** Review for compliance_failed flag */
  compliance_failed?: EvaluationReview | undefined;
  /** Reviews for metadata values, keyed by metadata field name */
  metadata?: { [key: string]: EvaluationReview } | undefined;
  /** Reviews for scorecard findings, keyed by metric_key */
  findings?: { [key: string]: EvaluationFindingReview } | undefined;
}

export interface EvaluationReviews_MetadataEntry {
  key: string;
  value?: EvaluationReview | undefined;
}

export interface EvaluationReviews_FindingsEntry {
  key: string;
  value?: EvaluationFindingReview | undefined;
}

/** EvaluationFindingReviewInput is the client-facing payload for reviewer edits to a scorecard finding. */
export interface EvaluationFindingReviewInput {
  raw_score?: number | undefined;
  evidence?: EvaluationEvidence | undefined;
}

/** EvaluationFindingReview is the stored reviewer edit record for a scorecard finding. */
export interface EvaluationFindingReview {
  raw_score?: number | undefined;
  evidence?: EvaluationEvidence | undefined;
  reviewed_by?: string | undefined;
  reviewed_at?: string | undefined;
}

/** Evidence links a finding to a specific asset and timestamp for auditability. */
export interface EvaluationEvidenceSpan {
  start_seconds?: number | undefined;
  end_seconds?: number | undefined;
}

export interface EvaluationEvidenceSnippet {
  text?: string | undefined;
  spans?: EvaluationEvidenceSpan[] | undefined;
}

/** Evidence links a finding to a specific asset and timestamp for auditability. */
export interface EvaluationEvidence {
  asset_id?: string | undefined;
  snippet?: string | undefined;
  snippets?: EvaluationEvidenceSnippet[] | undefined;
}

/** Finding records a single metric measurement within an evaluation. */
export interface EvaluationFinding {
  metric_key?: string | undefined;
  /** Normalized 0.0 - 1.0 */
  raw_score?: number | undefined;
  evidence?: EvaluationEvidence | undefined;
  /** Extensible: tones, emotions, etc. */
  metadata?: { [key: string]: any } | undefined;
}

/** Evaluation represents a single call quality review instance. */
export interface Evaluation {
  id?: string | undefined;
  organization_id?: string | undefined;
  program_id?: string | undefined;
  call_id?: string | undefined;
  evaluator_type?: EvaluatorType | undefined;
  scorecard_id?: string | undefined;
  findings?: EvaluationFinding[] | undefined;
  /** 0.0 - 1.0 */
  aggregate_score?: number | undefined;
  evaluated_at?: string | undefined;
  created_at?: string | undefined;
  updated_at?: string | undefined;
  created_by?: string | undefined;
  updated_by?: string | undefined;
  status?: EvaluationStatus | undefined;
  flags?: EvaluationFlags | undefined;
  previous_status?: EvaluationStatus | undefined;
  started_at?: string | undefined;
  completed_at?: string | undefined;
  cancelled_at?: string | undefined;
  version?: number | undefined;
  case_record_id?: string | undefined;
  agent_user_id?: string | undefined;
  call_direction?: CallDirection | undefined;
  /** Optional: explanations for flags and metadata values */
  reasons?: EvaluationReasons | undefined;
  /** Key-value pairs validated against scorecard ValidMetadataKeys */
  metadata?: { [key: string]: any } | undefined;
  /** Whether the evaluation is ineligible */
  ineligible?: boolean | undefined;
  /** Per-flag and per-metadata review records */
  reviews?: EvaluationReviews | undefined;
  /** Whether the evaluation needs human review */
  needs_review?: boolean | undefined;
  /** Workflow execution state for evaluation/re-evaluation runs */
  workflow_state?: WorkflowState | undefined;
  /** Call-sourced fields, caller-supplied (like call_direction / agent_user_id). */
  call_start_time?: string | undefined;
  /** Real call end */
  call_end_time?: string | undefined;
  /** Whether the call was a transfer */
  is_transfer_call?: boolean | undefined;
  /**
   * AQ-800: per-section score (0.0 - 1.0) keyed by scorecard section id, written by the scorer with
   * aggregate_score. Only sections active for this evaluation are present; absent = not applicable.
   */
  section_scores?: { [key: string]: number } | undefined;
}

export interface Evaluation_SectionScoresEntry {
  key: string;
  value: number;
}

export interface CreateEvaluationRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  call_id?: string | undefined;
  evaluator_type?: EvaluatorType | undefined;
  scorecard_id?: string | undefined;
  findings?: EvaluationFinding[] | undefined;
  aggregate_score?: number | undefined;
  evaluated_at?: string | undefined;
  status?: EvaluationStatus | undefined;
  flags?: EvaluationFlags | undefined;
  case_record_id?: string | undefined;
  agent_user_id?: string | undefined;
  call_direction?: CallDirection | undefined;
  /** Optional: explanations for flags and metadata values */
  reasons?: EvaluationReasons | undefined;
  /** Key-value pairs validated against scorecard ValidMetadataKeys */
  metadata?: { [key: string]: any } | undefined;
  /** Whether the evaluation is ineligible */
  ineligible?: boolean | undefined;
  /**
   * Call-sourced fields, caller-supplied (like call_direction / agent_user_id).
   * Optional + unvalidated; the server stores them as-is.
   */
  call_start_time?: string | undefined;
  call_end_time?: string | undefined;
  is_transfer_call?: boolean | undefined;
  /** Section id -> 0.0 - 1.0. Keys: non-empty, no '.' or '$'. */
  section_scores?: { [key: string]: number } | undefined;
}

export interface CreateEvaluationRequest_SectionScoresEntry {
  key: string;
  value: number;
}

export interface CreateEvaluationResponse {
  evaluation?: Evaluation | undefined;
}

export interface GetEvaluationRequest {
  organization_id?: string | undefined;
  evaluation_id?: string | undefined;
  /** When true, return the evaluation even if ineligible */
  ineligible?: boolean | undefined;
}

export interface GetEvaluationResponse {
  evaluation?: Evaluation | undefined;
}

export interface ListEvaluationsRequest {
  organization_id?: string | undefined;
  program_ids?: string[] | undefined;
  call_ids?: string[] | undefined;
  evaluator_type?: EvaluatorType | undefined;
  scorecard_id?: string | undefined;
  limit?: number | undefined;
  start_after_id?: string | undefined;
  statuses?: EvaluationStatus[] | undefined;
  case_record_ids?: string[] | undefined;
  agent_user_ids?: string[] | undefined;
  aggregate_score_gte?: number | undefined;
  aggregate_score_lte?: number | undefined;
  evaluated_at_after?: string | undefined;
  evaluated_at_before?: string | undefined;
  flag_escalation?: boolean | undefined;
  flag_compliance_failed?: boolean | undefined;
  /** Fields 16 (flag_ae) and 17 (flag_pqc) removed — now tracked in evaluation metadata */
  call_directions?: CallDirection[] | undefined;
  /** Deprecated (AQ-512): no longer drives the list window; use call_start_time_* / sort_by_call_start_time. */
  created_at_after?: string | undefined;
  /** Deprecated (AQ-512): no longer drives the list window; use call_start_time_* / sort_by_call_start_time. */
  created_at_before?: string | undefined;
  /** Optional: filter by metadata key-value pairs (AND logic). Empty value = existence check; single value = equality; comma-separated = $in match. HTTP dot notation: metadata.key=value. NOTE: comma-separated values do not support escaping — values must not themselves contain commas. */
  metadata?: { [key: string]: string } | undefined;
  /** Deprecated (AQ-512): no longer drives the list window; use call_start_time_* / sort_by_call_start_time. */
  sort_by_created_at?: Sort | undefined;
  /** Filter by needs_review flag */
  needs_review?: boolean | undefined;
  /** When true, include ineligible evaluations in results */
  ineligible?: boolean | undefined;
  /** Sort by aggregate_score (ASC or DESC). Mutually exclusive with sort_by_created_at — if both are set, sort_by_aggregate_score takes precedence. */
  sort_by_aggregate_score?: Sort | undefined;
  /** Filter by call start time (the call's real start). Replaces created_at_after for the list window. */
  call_start_time_after?: string | undefined;
  /** Filter by call start time (the call's real start). Replaces created_at_before for the list window. */
  call_start_time_before?: string | undefined;
  /** Sort by call_start_time (ASC or DESC). Mutually exclusive with sort_by_aggregate_score — score wins if both set. Replaces sort_by_created_at. */
  sort_by_call_start_time?: Sort | undefined;
  /**
   * AQ-497: whether the caller accepts a trimmed Evaluation (no findings/reviews/
   * evaluated_at). Default/unset/true = trimmed is fine, so the request may be served
   * from the faster ClickHouse evaluation_list store. Explicit false = the caller needs
   * findings/reviews (e.g. QC export computing section scores), so ClickHouse is skipped
   * and the request always goes to Mongo/Atlas, which returns the full Evaluation.
   */
  trimmed_evaluation?: boolean | undefined;
  /**
   * AQ-653: when true, return ONLY ineligible evaluations (ineligible = true).
   * Distinct from `ineligible` (field 26), which INCLUDES ineligible alongside
   * eligible. only_ineligible supersedes it: when set, the result is the
   * ineligible-only subset regardless of the include toggle.
   */
  only_ineligible?: boolean | undefined;
  /**
   * AQ-800: sort by one section's score. Takes precedence over sort_by_aggregate_score and
   * sort_by_call_start_time. Evaluations without that section sort last in both directions.
   */
  sort_by_section_score?: SectionScoreSort | undefined;
  /**
   * AQ-499: when true, return ONLY evaluations with a human review (finding, flag or
   * metadata). Served from ClickHouse evaluation_list only — Unavailable when the
   * request can't be served there (no Mongo/Atlas fallback).
   */
  only_reviewed?: boolean | undefined;
  /**
   * AQ-649: sort by call duration (call_end_time - call_start_time, whole seconds;
   * 0 when either time is missing). Precedence: section > aggregate_score >
   * call_duration > call_start_time. Served from ClickHouse evaluation_list only —
   * Unavailable when the request can't be served there (no Mongo/Atlas fallback).
   */
  sort_by_call_duration?: Sort | undefined;
}

export interface ListEvaluationsRequest_MetadataEntry {
  key: string;
  value: string;
}

/** SectionScoreSort orders the list by Evaluation.section_scores[section_id]. */
export interface SectionScoreSort {
  /** Scorecard section id: non-empty, no '.' or '$' */
  section_id?: string | undefined;
  /** SORT_ASC or SORT_DESC; SORT_NONE disables the section sort */
  sort?: Sort | undefined;
}

export interface ListEvaluationsResponse {
  evaluations?: Evaluation[] | undefined;
  /** Last entry ID for next page, empty if no more results */
  next_cursor?: string | undefined;
}

export interface GetEvaluationCountsRequest {
  organization_id?: string | undefined;
  program_ids?: string[] | undefined;
  call_ids?: string[] | undefined;
  agent_user_ids?: string[] | undefined;
  aggregate_score_gte?: number | undefined;
  aggregate_score_lte?: number | undefined;
  call_directions?: CallDirection[] | undefined;
  /** Deprecated (AQ-512): no longer drives the list window; use call_start_time_* / sort_by_call_start_time. */
  created_at_after?: string | undefined;
  /** Deprecated (AQ-512): no longer drives the list window; use call_start_time_* / sort_by_call_start_time. */
  created_at_before?: string | undefined;
  statuses?: EvaluationStatus[] | undefined;
  /** Dynamic metadata count facets. Key = metadata field name (must be registered & searchable). Value = comma-separated match values ($in). Each entry produces a group "metadata:<key>". HTTP dot notation: metadata_counts.key=values. Max 5 entries per request. NOTE: comma-separated values do not support escaping — values must not themselves contain commas. */
  metadata_counts?: { [key: string]: string } | undefined;
  /** When true, include ineligible evaluations in counts */
  ineligible?: boolean | undefined;
  /** Base metadata filter applied to ALL count groups (total + escalations/compliance/needs_review + metadata_counts facets), so tab counts reflect active metadata filters. Same semantics as ListEvaluationsRequest.metadata: empty value = existence; single = equality; comma-separated = $in. Key must be registered & searchable. HTTP dot notation: metadata.key=value. */
  metadata?: { [key: string]: string } | undefined;
  /** Call-start window; matches ListEvaluationsRequest.call_start_time_after so tab counts align with the list. */
  call_start_time_after?: string | undefined;
  /** Call-start window; matches ListEvaluationsRequest.call_start_time_before. */
  call_start_time_before?: string | undefined;
  /**
   * AQ-653: when true, count ONLY ineligible evaluations. Mirrors
   * ListEvaluationsRequest.only_ineligible so the tab counts align with the
   * "Show only ineligible evaluations" list view. Supersedes `ineligible` (18).
   */
  only_ineligible?: boolean | undefined;
  /** AQ-499: same semantics as ListEvaluationsRequest.only_reviewed; narrows every count group. */
  only_reviewed?: boolean | undefined;
}

export interface GetEvaluationCountsRequest_MetadataCountsEntry {
  key: string;
  value: string;
}

export interface GetEvaluationCountsRequest_MetadataEntry {
  key: string;
  value: string;
}

export interface EvaluationCountGroup {
  group?: string | undefined;
  count?: number | undefined;
}

export interface GetEvaluationCountsResponse {
  total_count?: number | undefined;
  groups?: EvaluationCountGroup[] | undefined;
}

export interface UpdateEvaluationRequest {
  organization_id?: string | undefined;
  evaluation_id?: string | undefined;
  findings?: EvaluationFinding[] | undefined;
  aggregate_score?: number | undefined;
  evaluated_at?: string | undefined;
  status?: EvaluationStatus | undefined;
  flags?: EvaluationFlags | undefined;
  /** Optional: explanations for flags and metadata values */
  reasons?: EvaluationReasons | undefined;
  call_direction?: CallDirection | undefined;
  /** Key-value pairs validated against scorecard ValidMetadataKeys */
  metadata?: { [key: string]: any } | undefined;
  /** Whether the evaluation is ineligible */
  ineligible?: boolean | undefined;
  /** Per-flag and per-metadata review inputs (reviewed_by/reviewed_at stamped server-side) */
  reviews?: EvaluationReviewsInput | undefined;
  /** Whether the evaluation needs human review */
  needs_review?: boolean | undefined;
  /** Resolved Compass user id for the agent; "" clears it, absent leaves it unchanged */
  agent_user_id?: string | undefined;
  /**
   * Call-sourced fields, caller-supplied (like call_direction / agent_user_id).
   * Optional + unvalidated; the server stores them as-is. Absent leaves unchanged.
   */
  call_start_time?: string | undefined;
  call_end_time?: string | undefined;
  is_transfer_call?: boolean | undefined;
  /** Section id -> 0.0 - 1.0; replaces the whole map. Empty leaves it unchanged (maps have no presence). */
  section_scores?: { [key: string]: number } | undefined;
}

export interface UpdateEvaluationRequest_SectionScoresEntry {
  key: string;
  value: number;
}

export interface UpdateEvaluationResponse {
  evaluation?: Evaluation | undefined;
}

export interface GetEvaluationAgentsRequest {
  organization_id?: string | undefined;
  /** When true, include ineligible evaluations in agent counts */
  ineligible?: boolean | undefined;
  /**
   * Date bounds mirror ListEvaluationsRequest.created_at_after/before so the agent
   * dropdown reflects the list page's active date filter AND the $group stays scoped
   * to that window (organization_id + created_at is index-backed), avoiding a
   * full-org collection scan (AQ-517).
   */
  created_at_after?: string | undefined;
  /** Deprecated (AQ-512): no longer drives the list window; use call_start_time_* / sort_by_call_start_time. */
  created_at_before?: string | undefined;
  /** Call-start window; mirrors the list page filter (AQ-512, was created_at_after per AQ-517). */
  call_start_time_after?: string | undefined;
  /** Call-start window; mirrors the list page filter. */
  call_start_time_before?: string | undefined;
}

/** EvaluationAgent represents a distinct agent who has at least one evaluation. */
export interface EvaluationAgent {
  /** Compass user ObjectID */
  user_id?: string | undefined;
  /** Display name resolved from user record */
  name?: string | undefined;
  /** Email resolved from user record */
  email?: string | undefined;
}

export interface GetEvaluationAgentsResponse {
  agents?: EvaluationAgent[] | undefined;
}

export interface GetEvaluationMetadataValuesRequest {
  organization_id?: string | undefined;
  /** metadata key (path param); validated against the registry */
  key?: string | undefined;
  /**
   * Call-start window; mirrors the list/analytics page filter so the offered
   * values match the rows currently in view (AQ-496 / AQ-512).
   */
  call_start_time_after?: string | undefined;
  call_start_time_before?: string | undefined;
  /**
   * q is an optional case-insensitive prefix filter (min 2 chars) for
   * high-cardinality keys (async search, e.g. Skill). Empty => full distinct list.
   */
  q?: string | undefined;
  /**
   * program_ids optionally narrows the values to specific programs, matching
   * the page's program filter.
   */
  program_ids?: string[] | undefined;
}

export interface GetEvaluationMetadataValuesResponse {
  values?: string[] | undefined;
}

/**
 * EvaluationService provides operations for managing QC evaluations.
 * Evaluations record call review results (AI or Human) against a scorecard.
 */
export interface EvaluationService {
  /** Create a new evaluation */
  createEvaluation(request: CreateEvaluationRequest): Promise<CreateEvaluationResponse>;
  /** Get an evaluation by ID */
  getEvaluation(request: GetEvaluationRequest): Promise<GetEvaluationResponse>;
  /** Update an existing evaluation */
  updateEvaluation(request: UpdateEvaluationRequest): Promise<UpdateEvaluationResponse>;
  /** List evaluations with optional filters */
  listEvaluations(request: ListEvaluationsRequest): Promise<ListEvaluationsResponse>;
  /** Get evaluation counts for QC tabs using the current base filters. */
  getEvaluationCounts(request: GetEvaluationCountsRequest): Promise<GetEvaluationCountsResponse>;
  /** Get distinct agents who have evaluations for an organization. */
  getEvaluationAgents(request: GetEvaluationAgentsRequest): Promise<GetEvaluationAgentsResponse>;
  /**
   * GetEvaluationMetadataValues returns the distinct values observed for one
   * metadata key on evaluations in the org + time window — backs the advanced
   * filter dropdowns (multiselect / single-select). Optional `q` turns it into a
   * case-insensitive prefix search for high-cardinality keys (async "type 2+
   * chars", e.g. Skill). The key is validated against the metadata registry
   * (searchable=true, domain=evaluation, this org) before use (AQ-496).
   */
  getEvaluationMetadataValues(
    request: GetEvaluationMetadataValuesRequest,
  ): Promise<GetEvaluationMetadataValuesResponse>;
}
